/**
 * 智能章节资源匹配服务（动态分词，无固定关键词）
 * 
 * 核心逻辑：
 * 1. 从招标模版Tab提取实际模版名称（每个项目不同）
 * 2. 对每个模版名称和章节标题分词
 * 3. 计算匹配度（基于分词交集）
 * 4. 返回匹配建议
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export interface ChapterInfo {
  id: string;
  title: string;
  description?: string;
  children?: ChapterInfo[];
}

export interface MatchResult {
  use_project_overview: boolean;
  use_tech_requirements: boolean;
  use_disqualification: boolean;
  use_templates: boolean;
  use_quotation: boolean;
  is_tech_requirements_suggested: boolean;
  is_quotation_suggested: boolean;
  is_templates_suggested: boolean;
  match_info?: {
    matched_template_name: string | null;
    confidence: number;
    match_score: number;
    reasoning: string;
  };
}

export interface SmartMatchResponse {
  success: boolean;
  data: Record<string, MatchResult>;
  message: string;
}

/**
 * 收集所有章节（包括子章节）
 */
function collectAllChapters(chapters: ChapterInfo[]): ChapterInfo[] {
  const result: ChapterInfo[] = [];
  
  function collect(items: ChapterInfo[]) {
    for (const item of items) {
      result.push({
        id: item.id,
        title: item.title,
        description: item.description || ''
      });
      
      if (item.children && item.children.length > 0) {
        collect(item.children);
      }
    }
  }
  
  collect(chapters);
  return result;
}

/**
 * 智能匹配章节资源
 * 
 * @param projectId 项目ID
 * @param chapters 章节树
 * @returns 匹配结果
 */
export async function smartMatchChapters(
  projectId: string,
  chapters: ChapterInfo[]
): Promise<Record<string, MatchResult>> {
  try {
    // 收集所有章节（包括子章节）
    const allChapters = collectAllChapters(chapters);
    
    console.log(`🔍 开始智能匹配: 项目${projectId}, 章节数${allChapters.length}`);
    
    // 调用后端智能匹配API
    const response = await fetch(
      `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/smart-match`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chapters: allChapters
        }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `智能匹配失败: ${response.status}`);
    }
    
    const result: SmartMatchResponse = await response.json();
    
    // 打印匹配统计
    const templateMatches = Object.values(result.data).filter(r => r.use_templates).length;
    console.log(`✅ 智能匹配完成: 共${allChapters.length}个章节, ${templateMatches}个匹配到招标模版`);
    
    // 打印匹配详情（调试用）
    Object.entries(result.data).forEach(([chapterId, matchResult]) => {
      if (matchResult.use_templates && matchResult.match_info) {
        const chapter = allChapters.find(c => c.id === chapterId);
        console.log(
          `  📋 ${chapter?.title} → ${matchResult.match_info.matched_template_name} (${matchResult.match_info.confidence}%)`
        );
      }
    });
    
    return result.data;
    
  } catch (error) {
    console.error('❌ 智能匹配失败:', error);
    throw error;
  }
}

/**
 * 格式化匹配结果为batch-init所需格式
 */
export function formatForBatchInit(matchResults: Record<string, MatchResult>) {
  const suggestions: Record<string, any> = {};
  
  for (const [chapterId, result] of Object.entries(matchResults)) {
    suggestions[chapterId] = {
      use_project_overview: result.use_project_overview,
      use_tech_requirements: result.use_tech_requirements,
      use_disqualification: result.use_disqualification,
      use_templates: result.use_templates,
      use_quotation: result.use_quotation,
      is_tech_requirements_suggested: result.is_tech_requirements_suggested,
      is_quotation_suggested: result.is_quotation_suggested,
      is_templates_suggested: result.is_templates_suggested
    };
  }
  
  return suggestions;
}
